// src/lib/referral.js
import {  db } from '../components/firebase';
import {
  doc, getDoc, setDoc, serverTimestamp, runTransaction
} from 'firebase/firestore';

/** ==== ?ref= из URL ==== */
export function storeRefFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const ref = p.get('ref');
    if (ref) {
      localStorage.setItem('referral_code', ref.toUpperCase());
      
    }
  } catch {}
}
export const getStoredRefCode = () => localStorage.getItem('referral_code') || '';
export const clearStoredRefCode = () => localStorage.removeItem('referral_code');

/** Generating readable code */
function randomCode(uid) {
  const base = btoa(uid).replace(/[^a-z0-9]/gi,'').slice(0,6).toUpperCase();
  const rnd  = Math.random().toString(36).slice(2,6).toUpperCase();
  return `${base}${rnd}`;
}

/** We instantly guarantee ourselves a referralCode (and a referralCodes/{code}->{uid} map) */
export async function ensureReferralCode(uid) {
  const uref = doc(db, 'users', uid);
  const us = await getDoc(uref);
  const cur = us.exists() ? us.data() : {};
  if (cur?.referralCode) return cur.referralCode;

  for (let i = 0; i < 6; i++) {
    const code = randomCode(uid);
    try {
      await setDoc(doc(db, 'referralCodes', code), { uid }); // create
      await setDoc(uref, { referralCode: code }, { merge: true });
      return code;
    } catch {
      
    }
  }
  const fallback = `U${uid.slice(0,6).toUpperCase()}${Date.now()%1000}`;
  await setDoc(doc(db, 'referralCodes', fallback), { uid });
  await setDoc(uref, { referralCode: fallback }, { merge: true });
  return fallback;
}

/** Basic profile initialization + instant ref code issuance*/
export async function postSignUpInit(user) {
  const uid = user.uid;
  const uref = doc(db, 'users', uid);
  const us = await getDoc(uref);
  if (!us.exists()) {
    await setDoc(uref, {
      email: user.email || null,
      displayName: user.displayName || null,
      role: 'client',
      plan: 'free',
      planSince: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true });
  }
  await ensureReferralCode(uid);
}

/** Link the code (without bonuses) - 1 time */
export async function claimReferral(uid, code) {
  const codeUp = String(code || '').trim().toUpperCase();
  if (!codeUp) throw new Error('Referral code required');

  const codeRef = doc(db, 'referralCodes', codeUp);
  const codeSnap = await getDoc(codeRef);
  if (!codeSnap.exists()) throw new Error('Invalid referral code');

  const referrerUid = (codeSnap.data() || {}).uid;
  if (referrerUid === uid) throw new Error('Self-referral not allowed');

  await runTransaction(db, async (tx) => {
    const userRef = doc(db, 'users', uid);
    const u = await tx.get(userRef);
    const d = u.exists() ? u.data() : {};
    if (d?.referredBy) throw new Error('Already referred');

    tx.set(userRef, {
      referredBy: referrerUid,
      referralAt: serverTimestamp(),
      referralCodeUsed: codeUp,
    }, { merge: true });

    const relRef = doc(db, 'referrals', referrerUid, 'referred', uid);
    tx.set(relRef, {
      status: 'pending',
      createdAt: serverTimestamp(),
    }, { merge: true });
  });

  return { referrerUid };
}

/** Mark the wallet as verified*/
export async function markOnchainOk(uid, wallet) {
  const wl = String(wallet || '').toLowerCase();
  if (!wl) throw new Error('wallet required');

  await setDoc(doc(db, 'walletClaims', `${uid}_${wl}`), {
    userId: uid,
    walletAddress: wl,
    claimedAt: new Date().toISOString(),
  });

  await setDoc(doc(db, 'users', uid, 'flags', 'onchain_ok'), {
    ok: true,
    at: serverTimestamp(),
  });
}

/** Confirm the connection and credit +50 to the inviter (one-time) */
export async function confirmReferralIfAny(uid) {
  const us = await getDoc(doc(db, 'users', uid));
  if (!us.exists()) return;
  const d = us.data() || {};
  const referrerUid = d?.referredBy;
  if (!referrerUid) return;

  // pending -> confirmed
  const relRef = doc(db, 'referrals', referrerUid, 'referred', uid);
  const rel = await getDoc(relRef);
  if (rel.exists() && rel.data()?.status !== 'confirmed') {
    await setDoc(relRef, { status: 'confirmed', confirmedAt: serverTimestamp() }, { merge: true });
  }

  // event at the inviter (idempotent id)
  const bonusEventId = `referral_bonus_${uid}`;
  await setDoc(doc(db, 'users', referrerUid, 'milesEvents', bonusEventId), {
    points: 50,
    source: 'referral_bonus',
    status: 'confirmed',
    meta: { referrerUid, referredUid: uid },
    createdAt: serverTimestamp(),
  }, { merge: false });
}

/** Link assembly (the link itself does not need to be stored in the database) */
export function buildReferralLink(code) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return code ? `${origin}/signup?ref=${code}` : '';
}

